import React, { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { MdEmail, MdLock, MdPerson } from "react-icons/md";
import { FiEye, FiEyeOff } from "react-icons/fi";
import Header from "../Header";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../firebase";

export default function SignUp() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [identifier, setIdentifier] = useState(""); // email OR phone
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (!firstName || !lastName || !identifier || !password) {
      setError("Please fill in all fields.");
      return;
    }

    try {
      const isEmail = /\S+@\S+\.\S+/.test(identifier);
      const fullName = `${firstName} ${lastName}`;

      if (isEmail) {
        // Email signup
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          identifier,
          password
        );
        const user = userCredential.user;

        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          first_name: firstName,
          last_name: lastName,
          name: fullName,
          email: identifier,
          role: "patient",
          createdAt: new Date(),
          firstLogin: true,
        });

        console.log(
          "📧 Sending signup email for:",
          firstName,
          lastName,
          identifier
        );

        // Trigger FastAPI signup email
        try {
          await fetch(
            "https://vpjc-api-805872224468.asia-south1.run.app/api/send-signup-email",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: identifier,
                first_name: firstName,
                last_name: lastName,
              }),
            }
          );
          console.log("📧 Signup email request sent!");
        } catch (err) {
          console.error("❌ Failed to send signup email:", err);
        }
      } else {
        // Phone signup
        const q = query(
          collection(db, "users"),
          where("phone", "==", identifier)
        );
        const existing = await getDocs(q);
        if (!existing.empty) {
          setError("Phone number already registered.");
          return;
        }

        const phoneUserRef = doc(collection(db, "users"));
        await setDoc(phoneUserRef, {
          uid: phoneUserRef.id,
          first_name: firstName,
          last_name: lastName,
          name: fullName,
          phone: identifier,
          password,
          role: "patient",
          createdAt: new Date(),
          firstLogin: true,
        });

        // If phone signup includes email, send FastAPI email
        // if (identifier.includes("@")) {
        //   console.log("📧 Sending signup email for:", firstName, lastName, identifier);
        //   try {
        //     await fetch("https://vpjc-api-805872224468.asia-south1.run.app/send-signup-email", {
        //       method: "POST",
        //       headers: { "Content-Type": "application/json" },
        //       body: JSON.stringify({
        //         email: identifier,
        //         first_name: firstName,
        //         last_name: lastName,
        //       }),
        //     });
        //     console.log("📧 Signup email request sent!");
        //   } catch (err) {
        //     console.error("❌ Failed to send signup email:", err);
        //   }
        // }
      }

      // Success message
      setSuccess("Signup successful! ✅ Confirmation email has been sent.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      console.error("❌ Firebase Signup Error:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("This email is already registered. Please login instead.");
        navigate("/login");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="flex justify-center items-center min-h-screen px-4 pt-24 pb-24 bg-gradient-to-br from-white via-blue-50 to-slate-50">
        <div className="w-full max-w-3xl mt-6 bg-white shadow-xl rounded-2xl overflow-hidden flex flex-col md:flex-row">
          <div className="md:w-1/2 flex items-center justify-center p-2 bg-white">
            <img
              src="./signUpIllstn.avif"
              alt="Illustration"
              className="w-full max-w-[600px] h-auto object-contain"
            />
          </div>

          <div className="md:w-1/2 p-6 flex flex-col justify-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#204080] mb-6 text-center md:text-left">
              Step Into Better Health
            </h2>

            {success && (
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg mb-4 text-sm text-center">
                {success}
              </div>
            )}
            {error && (
              <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-4">
              {/* First Name */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                  <MdPerson />
                </span>
                <input
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#204080] focus:ring-0 bg-gray-50 text-gray-800 placeholder-gray-400 transition-all"
                />
              </div>

              {/* Last Name */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                  <MdPerson />
                </span>
                <input
                  type="text"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#204080] focus:ring-0 bg-gray-50 text-gray-800 placeholder-gray-400 transition-all"
                />
              </div>

              {/* Email / Phone */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                  <MdEmail />
                </span>
                <input
                  type="text"
                  placeholder="Email Address or Phone Number"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#204080] focus:ring-0 bg-gray-50 text-gray-800 placeholder-gray-400 transition-all"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                  <MdLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-10 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#204080] focus:ring-0 bg-gray-50 text-gray-800 placeholder-gray-400 transition-all"
                />
                {password.length > 0 && (
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-base cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 text-center md:text-left">
                By signing up, you agree to our{" "}
                <span
                  onClick={() => navigate("/terms-and-conditions")}
                  className="text-[#204080] font-semibold hover:underline cursor-pointer"
                >
                  Terms & Conditions
                </span>{" "}
                and{" "}
                <span
                  onClick={() => navigate("/privacy-policy")}
                  className="text-[#204080] font-semibold hover:underline cursor-pointer"
                >
                  Privacy Policy
                </span>
                .
              </p>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 rounded-lg font-semibold transition-all ${
                  loading
                    ? "bg-gray-300 cursor-not-allowed text-gray-600"
                    : "bg-[#204080] text-white hover:bg-blue-900"
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      />
                    </svg>
                    <span>Signing up...</span>
                  </div>
                ) : (
                  "Sign Up"
                )}
              </button>
            </form>

            <p className="text-center text-gray-600 mt-4 md:text-left">
              Already have an account?{" "}
              <span
                onClick={() => navigate("/login")}
                className="text-[#204080] font-semibold hover:underline cursor-pointer"
              >
                Login
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
